import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, User, MapPin, ArrowRight, Calendar as CalendarIcon } from 'lucide-react';
import CommonLoader from '../CommonLoader';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatClock, formatTime } from '../../utils/clock';

// Presentational — both appointment lists (upcoming = Booked, past = all) arrive
// via props from the patient dashboard socket. The tab switch is now purely
// client-side (no refetch).
const AppointmentsScreen = ({ appointmentsUpcoming, appointmentsPast, loading }) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState('upcoming');

  const appointmentsData = activeTab === 'upcoming' ? appointmentsUpcoming : appointmentsPast;
  const patientAppointmentsLoading = loading;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);


  const formattedDate = currentTime.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const formattedTime = formatTime(currentTime, { timeZone: 'Asia/Kolkata' });

  // Get appointments from API and limit to 6
  const appointments = appointmentsData?.data?.slice(0, 6) || [];

  const renderAppointmentItem = (appointment, index) => {
    // Format date from API
    const apptDate = new Date(appointment.scheduledDate);
    const dateStr = apptDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const itemKey = appointment.appointmentId || index;
    return (
      <motion.div
        key={index}
        data-testid={`appointments-screen-item-${itemKey}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: index * 0.05 }}
        className="bg-gradient-to-br from-white to-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300"
      >
        <div className="flex items-start space-x-2 sm:space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-full flex items-center justify-center">
              <User data-testid={`appointments-screen-item-${itemKey}-avatar-icon`} className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 mb-2">
              <h4 data-testid={`appointments-screen-item-${itemKey}-doctor-name`} className="text-xs sm:text-sm font-bold text-gray-900 truncate">
                {appointment.doctorName}
              </h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-xs">
              <div className="flex items-center space-x-1 text-gray-500">
                <Calendar className="w-3 h-3 text-[#4ABDBD] flex-shrink-0" />
                <span data-testid={`appointments-screen-item-${itemKey}-date`} className="truncate">{dateStr}</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-500">
                <Clock className="w-3 h-3 text-[#4ABDBD] flex-shrink-0" />
                <span data-testid={`appointments-screen-item-${itemKey}-time`} className="truncate">{formatClock(appointment.timeSlot)}</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-500 sm:col-span-2">
                <MapPin className="w-3 h-3 text-[#4ABDBD] flex-shrink-0" />
                <span data-testid={`appointments-screen-item-${itemKey}-address`} className="truncate">{appointment.addressTypeName}</span>
              </div>
              {appointment.status && (
                <div className="flex items-center space-x-1 sm:col-span-2">
                  <span
                    data-testid={`appointments-screen-item-${itemKey}-status-badge`}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${appointment.status === 'Booked'
                    ? 'bg-green-100 text-green-800'
                    : appointment.status === 'Completed'
                      ? 'bg-blue-100 text-blue-800'
                      : appointment.status === 'Cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {t(`dashboard.${appointment.status}`)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
        .custom-scrollbar::-webkit-scrollbar-button {
          display: none;
        }
      `}</style>
      <motion.div
        data-testid="appointments-screen"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl p-3 sm:p-4 md:p-6 border border-gray-200/50 shadow-md backdrop-blur-sm h-full"
      >
        <div data-testid="appointments-screen-header" className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 data-testid="appointments-screen-heading" className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center">
            <CalendarIcon data-testid="appointments-screen-heading-icon" className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
            <span className="hidden xs:inline">{t('dashboard.appointments_screen')}</span>
            <span className="xs:hidden">{t('dashboard.appointments_screen')}</span>
          </h3>
          <motion.button
            data-testid="appointments-screen-view-all-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/myAppointment")}
            className="text-xs font-medium text-[#4ABDBD] hover:text-[#3A9B9B] transition-colors duration-200 flex items-center"
          >
            {/* Desktop View */}
            <span className="hidden sm:inline-flex items-center">
              {t('dashboard.view_all')}
              <ArrowRight className="w-3 h-3 ml-1" />
            </span>

            {/* Mobile View */}
            <span className="sm:hidden underline flex items-center">
              {t('dashboard.view_all')}
              <ArrowRight className="w-3 h-3 ml-1" />
            </span>
          </motion.button>
        </div>

        <div className="w-full">
          <div data-testid="appointments-screen-tabs" className="flex space-x-1 bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl p-1 shadow border border-gray-200 mb-4 sm:mb-6">
            <button
              data-testid="appointments-screen-tab-upcoming"
              onClick={() => setActiveTab('upcoming')}
              className={`relative flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-300 ${activeTab === 'upcoming'
                ? 'text-white'
                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                }`}
            >
              {activeTab === 'upcoming' && (
                <motion.div
                  layoutId="activeAppointmentScreenTab"
                  className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-20">{t('dashboard.upcoming')}</span>
            </button>
            <button
              data-testid="appointments-screen-tab-past"
              onClick={() => setActiveTab('past')}
              className={`relative flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-300 ${activeTab === 'past'
                ? 'text-white'
                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                }`}
            >
              {activeTab === 'past' && (
                <motion.div
                  layoutId="activeAppointmentScreenTab"
                  className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-20">{t('dashboard.past')}</span>
            </button>
          </div>

          <AnimatePresence mode="wait">
            {patientAppointmentsLoading ? (
              <div data-testid="appointments-screen-loading" className="flex flex-col items-center justify-center py-12">
                <CommonLoader />
                <span data-testid="appointments-screen-loading-text" className="text-sm text-gray-600 mt-2">{t('dashboard.loading_appointments')}</span>
              </div>
            ) : (
              <motion.div
                key={activeTab}
                data-testid={`appointments-screen-content-${activeTab}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="space-y-3 sm:space-y-4"
              >
                {appointments.length > 0 ? (
                  <div data-testid="appointments-screen-list" className="max-h-60 sm:max-h-80 overflow-y-auto p-1 sm:p-2 space-y-2 sm:space-y-3 custom-scrollbar">
                    {appointments.map((appointment, index) => renderAppointmentItem(appointment, index))}
                  </div>
                ) : (
                  <div data-testid="appointments-screen-empty-state" className="text-center py-8">
                    <Calendar data-testid="appointments-screen-empty-icon" className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p data-testid="appointments-screen-empty-text" className="text-sm text-gray-500 font-medium">
                      {activeTab === 'upcoming' ? t('dashboard.no_upcoming_appointments') : t('dashboard.no_past_appointments')}
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
};

export default AppointmentsScreen;
